import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { Button, Heading, IconButton, Text, TextField } from '@praetoid/ui';
import { useForm } from '@tanstack/react-form';
import { Link, createFileRoute } from '@tanstack/react-router';

import { useAuth } from '~/context/useAuth';
import { parseError, parseFieldError } from '~/lib/errors';
import { validateSignupField } from '~/routes/_auth/-validation';

export interface SignupSearchParams {
	fullName?: string;
	email?: string;
	organizationName?: string;
}

export const Route = createFileRoute('/_auth/signup')({
	validateSearch: (search): SignupSearchParams => {
		return search;
	},
	component: RouteComponent
});

function RouteComponent() {
	const { signupMutation } = useAuth();
	const { fullName, email, organizationName } = Route.useSearch();
	const { mutateAsync: signUp, isPending, error } = signupMutation;

	const form = useForm({
		defaultValues: {
			full_name: fullName ?? '',
			identifier: email ?? '',
			password: ''
		},
		onSubmit: async ({ value }) => {
			try {
				await signUp({ data: value });
			} catch (error) {
				console.error(error);
			}
		}
	});

	const generalError = parseError(error);

	return (
		<main className="flex h-screen w-screen">
			<div className="absolute top-4 left-4">
				<IconButton variant="surface" radius="large" size="2" asChild>
					<Link to="/">
						<ArrowLeftIcon className="fill-gray-11" />
					</Link>
				</IconButton>
			</div>

			<div className="ui-gradient flex h-screen w-full flex-1 justify-center px-9">
				<div className="flex w-full flex-col items-center justify-center gap-4 py-12">
					<div className="flex w-full max-w-lg flex-col gap-4">
						<div className="flex flex-col justify-center gap-2">
							<Heading className="text-3xl">Create an account</Heading>

							{organizationName ? (
								<Text
									size="2"
									weight="medium"
									as="p"
									className="text-foreground-secondary"
									color="gray"
								>
									You&apos;ve been invited to join{' '}
									<Text weight="bold" color="gray">
										{organizationName}
									</Text>
								</Text>
							) : (
								<Text
									size="2"
									weight="medium"
									as="p"
									className="text-foreground-secondary"
									color="gray"
								>
									Already have an account?{' '}
									<Text className="underline" asChild>
										<Link to="/signin">Sign in</Link>
									</Text>
								</Text>
							)}
						</div>

						<form
							className="flex w-full flex-col gap-4"
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								form.handleSubmit();
							}}
						>
							{generalError && (
								<div className="bg-redA-2 text-red-11 rounded-md p-3 text-sm">{generalError}</div>
							)}

							<form.Field name="full_name">
								{(field) => {
									const serverError = parseFieldError(error, 'full_name');
									const serverErrorEmail = parseFieldError(error, 'full_name');
									const displayError =
										field.state.meta.errors[0] || serverError || serverErrorEmail;

									return (
										<div className="flex flex-col gap-3">
											<Text size="2" weight="medium">
												Full name
											</Text>
											<TextField.Root
												id="name"
												required
												placeholder="Enter full name"
												size="3"
												variant="soft"
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
											{displayError && (
												<Text size="1" className="text-red-11">
													{displayError}
												</Text>
											)}
										</div>
									);
								}}
							</form.Field>

							<div className="flex gap-4">
								<form.Field
									name="identifier"
									validators={{
										onChange: ({ value }) => validateSignupField('identifier', value)
									}}
								>
									{(field) => {
										const serverError = parseFieldError(error, 'identifier');
										const serverErrorEmail = parseFieldError(error, 'email');
										const displayError =
											field.state.meta.errors[0] || serverError || serverErrorEmail;

										return (
											<div className="flex flex-1 flex-col gap-3">
												<Text size="2" weight="medium">
													Email
												</Text>
												<TextField.Root
													placeholder="Enter email address"
													size="3"
													variant="soft"
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													disabled={!!email}
													onChange={(e) => field.handleChange(e.target.value)}
												/>
												{displayError && (
													<Text size="1" className="text-red-11">
														{displayError}
													</Text>
												)}
											</div>
										);
									}}
								</form.Field>

								<form.Field
									name="password"
									// validators={{
									// 	onChange: ({ value }) => validateSignupField('password', value)
									// }}
								>
									{(field) => {
										const serverError = parseFieldError(error, 'password');
										const displayError = field.state.meta.errors[0] || serverError;

										return (
											<div className="flex flex-1 flex-col gap-3">
												<Text size="2" weight="medium">
													Password
												</Text>
												<TextField.Root
													placeholder="Enter password"
													size="3"
													variant="soft"
													type="password"
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
												/>
												{displayError && (
													<Text size="1" className="text-red-11">
														{displayError}
													</Text>
												)}
											</div>
										);
									}}
								</form.Field>
							</div>

							<form.Subscribe selector={(state) => state.canSubmit}>
								{(canSubmit) => (
									<Button
										type="submit"
										color="accent"
										size="3"
										disabled={!canSubmit || isPending}
										className="mt-2"
									>
										{isPending ? 'Creating account...' : 'Create account'}
									</Button>
								)}
							</form.Subscribe>
						</form>
					</div>

					<div className="flex flex-col gap-4">
						{/* <div className="flex items-center gap-2">
							<Separator className="flex-1" />
							<Text size="1" weight="medium" className="text-foreground-secondary uppercase">
								Or
							</Text>
							<Separator className="flex-1" />
						</div>

						<Button variant="outline">Continue with Google</Button> */}
					</div>
				</div>
			</div>

			<div className="relative hidden h-full w-full flex-1 flex-col items-center justify-center overflow-hidden border-l sm:flex"></div>
		</main>
	);
}
